import { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Activity, RefreshCw, Search, X } from 'lucide-react';
import { useSignals, useAllSignals } from '../../api/hooks';
import DataTable from '../../components/tables/DataTable';
import { SectionHeader, SignalBadge, EmptyState, Spinner } from '../../components/ui';
import { ROUTES } from '../../routes/constants';
import { fmtDateTime, fmtPrice, cn } from '../../lib/utils';

function getTodayIstDate() {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return fmt.format(new Date());
}

export default function SignalsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [depId, setDepId]     = useState(searchParams.get('deployment') || '');
  const [activeId, setActive] = useState(searchParams.get('deployment') || '');
  const [activeTab, setActiveTab] = useState('external');
  const todayIst = getTodayIstDate();
  const [fromDate, setFromDate] = useState(todayIst);
  const [toDate, setToDate] = useState(todayIst);
  const [appliedRange, setAppliedRange] = useState({ from: todayIst, to: todayIst });

  const isFiltered = !!activeId;
  const dateQueryParams = useMemo(
    () => ({
      from_date: appliedRange.from || todayIst,
      to_date: appliedRange.to || todayIst,
      strategy_source: activeTab === 'external' ? 'EXTERNAL' : 'INTERNAL',
    }),
    [appliedRange, todayIst, activeTab],
  );

  const { data: allData, isLoading: allLoading, refetch: refetchAll, error: allError } = useAllSignals(dateQueryParams);
  const { data: filteredData, isLoading: filteredLoading, refetch: refetchFiltered, error: filteredError } = useSignals(activeId, dateQueryParams);

  const rawRows   = (isFiltered ? filteredData : allData) || [];
  const isLoading = isFiltered ? filteredLoading : allLoading;
  const error     = isFiltered ? filteredError   : allError;
  const refetch   = isFiltered ? refetchFiltered : refetchAll;

  // Newest first
  const rows = useMemo(
    () => [...rawRows].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    [rawRows],
  );

  const handleLoad  = () => { if (depId.trim()) setActive(depId.trim()); };
  const handleClear = () => { setDepId(''); setActive(''); };
  const handleApplyDates = () => {
    const start = fromDate || todayIst;
    const end = toDate || todayIst;
    if (start > end) return;
    setAppliedRange({ from: start, to: end });
  };
  const handleResetDates = () => {
    setFromDate(todayIst);
    setToDate(todayIst);
    setAppliedRange({ from: todayIst, to: todayIst });
  };

  // Deployment ID column shown only in unfiltered mode
  const depIdColumn = useMemo(() => ({
    id: 'deployment_id',
    accessorKey: 'deployment_id',
    header: 'Deployment',
    size: 100,
    cell: ({ getValue }) => <span className="font-mono text-txt-muted text-xs">{getValue() ?? '—'}</span>,
  }), []);

  const coreColumns = useMemo(() => [
    {
      accessorKey: 'strategy_name',
      header: 'Strategy',
      size: 150,
      cell: ({ getValue }) => <span className="font-mono text-txt-secondary text-xs">{getValue() || '—'}</span>,
    },
    {
      accessorKey: 'created_at',
      header: 'Signal Time',
      size: 160,
      cell: ({ getValue }) => <span className="font-mono text-txt-muted">{fmtDateTime(getValue())}</span>,
    },
    {
      accessorKey: 'direction',
      header: 'Direction',
      size: 110,
      cell: ({ getValue }) => <SignalBadge direction={getValue()} />,
    },
    {
      accessorKey: 'option_symbol',
      header: 'Option Symbol',
      size: 180,
      cell: ({ getValue }) => <span className="font-mono text-txt-primary text-xs">{getValue() || '—'}</span>,
    },
    {
      accessorKey: 'action',
      header: 'Action',
      size: 80,
      cell: ({ getValue }) => <span className="font-mono text-txt-secondary">{getValue() || '—'}</span>,
    },
    {
      accessorKey: 'signal_price',
      header: 'Signal Price',
      size: 110,
      cell: ({ getValue }) => (
        <span className="font-mono font-semibold text-txt-primary">
          {getValue() ? `₹${fmtPrice(getValue())}` : '—'}
        </span>
      ),
    },
    {
      accessorKey: 'alert_quantity',
      header: 'Alert Qty',
      size: 70,
      cell: ({ getValue }) => <span className="font-mono text-txt-secondary">{getValue() ?? '—'}</span>,
    },
    {
      accessorKey: 'skip_reason',
      header: 'Skip Reason',
      size: 160,
      cell: ({ getValue }) => <span className="font-mono text-warn text-2xs">{getValue() || '—'}</span>,
    },
    {
      accessorKey: 'was_dispatched',
      header: 'Dispatched',
      size: 100,
      cell: ({ getValue, row }) => {
        const v    = getValue();
        const skip = row.original.skip_reason;
        return (
          <span className={cn(
            'badge border text-2xs',
            v    ? 'bg-bull/10 border-bull/30 text-bull' :
            skip ? 'bg-warn/10 border-warn/30 text-warn' :
                   'bg-bg-hover border-bg-border text-txt-muted',
          )}>
            {v ? 'yes' : skip ? 'skipped' : 'no'}
          </span>
        );
      },
    },
  ], []);

  const columns = useMemo(
    () => isFiltered ? coreColumns : [depIdColumn, ...coreColumns],
    [isFiltered, coreColumns, depIdColumn],
  );

  return (
    <div className="max-w-7xl space-y-5">
      <SectionHeader
        title="Signals"
        subtitle="Strategy entry/exit signals"
        actions={
          <button onClick={refetch} className="btn-ghost flex items-center gap-1.5 text-xs">
            <RefreshCw size={12} /> Refresh
          </button>
        }
      />

      {/* Filter bar */}
      <div className="panel p-4 flex items-center gap-3 flex-wrap">
        <Activity size={13} className="text-amber shrink-0" />
        <span className="text-xs text-txt-muted font-mono">Filter by Deployment ID:</span>
        <input
          value={depId}
          onChange={(e) => setDepId(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleLoad()}
          placeholder="optional"
          className="input-base w-32 text-xs"
        />
        <button
          onClick={handleLoad}
          disabled={!depId.trim()}
          className="btn-primary text-xs flex items-center gap-1.5 py-1.5"
        >
          <Search size={11} /> Load
        </button>
        {activeId && (
          <button onClick={handleClear} className="btn-ghost text-xs flex items-center gap-1 py-1.5">
            <X size={11} /> Clear
          </button>
        )}
        <span className="text-xs text-txt-muted font-mono ml-2">From:</span>
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          className="input-base w-40 text-xs"
        />
        <span className="text-xs text-txt-muted font-mono">To:</span>
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          className="input-base w-40 text-xs"
        />
        <button
          onClick={handleApplyDates}
          disabled={!fromDate || !toDate || fromDate > toDate}
          className="btn-primary text-xs py-1.5"
        >
          Apply
        </button>
        <button onClick={handleResetDates} className="btn-ghost text-xs py-1.5">
          Reset
        </button>
      </div>

      {/* Status label */}
      <p className="text-2xs font-mono text-txt-muted px-1">
        {isFiltered
          ? `Showing records for deployment #${activeId}`
          : 'Showing latest records across all deployments'}
        {` • Date: ${appliedRange.from} to ${appliedRange.to} (IST)`}
      </p>

      {/* Table */}
      <div className="panel p-4">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <div className="inline-flex items-center rounded-lg border border-bg-border bg-bg-elevated p-1">
            <button
              onClick={() => setActiveTab('external')}
              className={cn(
                'px-3 py-1.5 text-xs font-mono rounded-md transition-colors',
                activeTab === 'external'
                  ? 'bg-bg-hover text-amber border border-amber/30'
                  : 'text-txt-muted hover:text-txt-primary',
              )}
            >
              External Webhook Signals
            </button>
            <button
              onClick={() => setActiveTab('internal')}
              className={cn(
                'px-3 py-1.5 text-xs font-mono rounded-md transition-colors',
                activeTab === 'internal'
                  ? 'bg-bg-hover text-amber border border-amber/30'
                  : 'text-txt-muted hover:text-txt-primary',
              )}
            >
              Internal Strategy Signals
            </button>
          </div>
          <button
            onClick={refetch}
            className="btn-ghost text-2xs flex items-center gap-1.5"
          >
            <RefreshCw size={11} /> Refresh
          </button>
        </div>
        {isLoading ? (
          <div className="flex justify-center py-12"><Spinner size={18} /></div>
        ) : error ? (
          <EmptyState title="Could not load signals" description={error.message} />
        ) : rows.length === 0 ? (
          <EmptyState
            title={isFiltered ? 'No signals for this deployment' : 'No signals yet'}
            description={isFiltered
              ? 'Try clearing the deployment filter to see all signals.'
              : "Signals fire when a deployed strategy's rules match on candle close."}
            action={isFiltered ? (
              <button onClick={handleClear} className="btn-secondary mt-4 text-xs">
                Clear filter
              </button>
            ) : (
              <button onClick={() => navigate(ROUTES.STRATEGIES)} className="btn-secondary mt-4 text-xs">
                View strategies
              </button>
            )}
          />
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            searchable={true}
            searchPlaceholder="Search signals (strategy, symbol, action)…"
            emptyText={isFiltered ? 'No signals for this deployment' : 'No signals found'}
            pageSize={25}
          />
        )}
      </div>
    </div>
  );
}
